import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeSlash, CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [info, setInfo] = useState({ email: "", purpose: "reset" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api.get(`/auth/reset-token/${token}`)
      .then(({ data }) => {
        if (data.valid) { setInfo({ email: data.email, purpose: data.purpose }); setStatus("valid"); }
        else setStatus("invalid");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { token, password });
      toast.success("Password set. You can now sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not set password");
    } finally {
      setBusy(false);
    }
  };

  const isInvite = info.purpose === "invite";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A] px-6">
      <div className="w-full max-w-md bg-white border border-border p-8" data-testid="set-password-card">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-1">Dataset Delta</div>

        {status === "checking" && (
          <p className="font-mono text-sm text-muted-foreground animate-pulse mt-4">Validating link…</p>
        )}

        {status === "invalid" && (
          <div className="mt-4" data-testid="set-password-invalid">
            <div className="flex items-center gap-2 text-destructive mb-2">
              <WarningCircle size={22} weight="fill" />
              <h1 className="font-heading text-xl font-bold">Link expired or invalid</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              This password link is no longer valid. Ask an administrator to resend your invite, or use
              “Forgot password” on the sign-in page.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 mt-5 text-primary font-semibold text-sm hover:underline">
              Go to sign in <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {status === "valid" && (
          <form onSubmit={submit} className="mt-4 space-y-4" data-testid="set-password-form">
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle size={22} weight="fill" />
              <h1 className="font-heading text-xl font-bold tracking-tight">
                {isInvite ? "Welcome — set your password" : "Choose a new password"}
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">
              For <span className="font-mono text-foreground">{info.email}</span>
            </p>

            <div className="space-y-1.5">
              <Label htmlFor="pw">New password</Label>
              <div className="relative">
                <Input
                  id="pw"
                  data-testid="set-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  data-testid="set-password-toggle"
                  onClick={() => setShow((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pw2">Confirm password</Label>
              <Input
                id="pw2"
                data-testid="set-password-confirm"
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                required
              />
            </div>

            <Button type="submit" data-testid="set-password-submit" disabled={busy} className="w-full gap-2 h-11">
              {busy ? "Saving…" : "Set password"}
              {!busy && <ArrowRight size={18} />}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
